/* eslint-disable @typescript-eslint/no-explicit-any */
import { cn } from "@/infrastructure/lib/utils";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWorkspace } from "@/shared/hooks/api/useWorkspace";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { Button } from "@/shared/components/ui/button";
import { Users, AlertCircle, MessageSquare, Plus, Share, ExternalLink, ChevronDown, MoreHorizontal, Trash2, Edit2 } from "lucide-react";
import ChatMessage from "./ChatMessage";
import { ChatComposer } from "./ChatComposer";
import { hasPendingSummary, mergeAttachmentUpdates, splitBySize } from "../utils/chatAttachments";
import type { ChatAttachment } from "../types/chatBase";
import ChatList from "./ChatList";
import { ThinkingTrace } from "./ThinkingTrace";
import { StreamingMarkdown } from "./StreamingMarkdown";
import { ThinkingTraceEntry } from "../types/playground";
import {
  arrangeHistory,
  arrangeTurn,
  lastBoundary,
  splitTrace,
  type JoinedMessage,
  type StoredTurnTrace,
} from "../utils/turnTimeline";
import {
  sanitizeForPdf,
  parseContentSegments,
  PDF_COLORS,
  drawFooters,
  renderHeader,
  renderMessageBlock,
} from "../utils/pdfExport";
import type { PdfLayout } from "../utils/pdfExport";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/shared/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";

import { PlaygroundMultiChatService } from "../services/chatBaseService";
import { usePlaygroundSSE } from "../hooks/usePlaygroundSSE";
import { ChatBaseProvider, useChatBaseContext } from '../context/ChatBaseContext';
import { ChatMessage as ChatMessageType, UserProfile } from "../types/playground";
import { useToast } from "@/shared/components/ui/use-toast";
import { ToastAction } from "@/shared/components/ui/toast";
import { useBackgroundTasks } from "../hooks/useBackgroundTasks";
import { BackgroundTasksIndicator, BackgroundTasksMarker } from "./BackgroundTasksIndicator";
import { analyticsService } from "@/shared/services/analytics";
import { EmbedChatFlyout } from "./EmbedChatFlyout";
import { McpToolsDialog } from "./McpToolsDialog";
import { IntegrationBaseService } from "@/features/Integrations/services/integrationsBaseService";
import { OrganizationService } from "@/features/Organization/services/organizationService";
import { logger } from "@/infrastructure/lib/logger";
import { applyToolProgress, stripToolProgress } from "../utils/traceProgress";
import { useOrganization } from "@/shared/hooks/api/useOrganization";

// Tipo para integração com tools MCP
interface McpIntegrationInfo {
  id: string;
  name: string;
  label: string;
  /** Nome desta instalação — o que distingue duas do mesmo provedor (AB#86575). */
  title: string | null;
  logo_url: string | null;
  tools: { name: string; description: string }[];
}

// De quanto em quanto tempo a conversa é consultada enquanto um resumo de anexo está em preparo.
const SUMMARY_REFRESH_MS = 8000;

// Componente interno que usa o contexto
function PlaygroundViewInternal() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const { data: userProfile } = useOrganization();
  const { toast } = useToast();
  const [input, setInput] = useState("");
  const [mcpIntegrations, setMcpIntegrations] = useState<McpIntegrationInfo[]>([]);
  const [mcpToolsOpen, setMcpToolsOpen] = useState(false);
  const [defaultModelName, setDefaultModelName] = useState<string | null>(null);
  const [hasLlmConfigured, setHasLlmConfigured] = useState<boolean>(true);

  // Map i18n language to locale format
  const getLocale = () => {
    const lang = i18n.language;
    if (lang.startsWith('pt')) return 'pt-BR';
    if (lang.startsWith('en')) return 'en-US';
    return 'en-US'; // Default fallback
  };
  
  // Sistema de múltiplos chats
  const {
    chats,
    selectedChatId,
    selectedChat,
    isSidebarOpen,
    createChat,
    selectChat,
    toggleSidebar,
    getChatHistory,
    updateChatMetadata,
    addMessageToCache,
    clearHistoryCache,
    renameChat,
    deleteChat,
  } = useChatBaseContext();

  const [_isLoading, _setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [_connectionStatus, _setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected' | 'reconnecting'>('disconnected');
  const [_userProfiles, _setUserProfiles] = useState<{ [key: string]: UserProfile }>({});
  const [_showEmptyState, setShowEmptyState] = useState(true);
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  // Arquivos do botão + ainda não enviados (AB#87121): sobem junto com a próxima mensagem.
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [messageAuthors] = useState<Record<string, UserProfile>>({});
  const [isAIThinking, setIsAIThinking] = useState(false);
  // Structured trace being built while the assistant streams. Mirrors what the
  // API persists on the `sender='thinking'` row at the end of the turn.
  const [currentTrace, setCurrentTrace] = useState<ThinkingTraceEntry[]>([]);
  // Final-answer text being typed live (matches the API's `fullText`).
  const [finalStreamingText, setFinalStreamingText] = useState('');
  const currentTraceRef = useRef<ThinkingTraceEntry[]>([]);
  // Mensagens mandadas com o turno rodando e o ponto do rastro em que entraram (AB#86889, 87271).
  const [joinedMessages, setJoinedMessages] = useState<JoinedMessage[]>([]);
  const joinedMessagesRef = useRef<JoinedMessage[]>([]);
  const updateJoinedMessages = (next: JoinedMessage[]) => {
    joinedMessagesRef.current = next;
    setJoinedMessages(next);
  };
  const joinedIds = useMemo(() => new Set(joinedMessages.map((j) => j.messageId)), [joinedMessages]);
  const messageById = useMemo(
    () => new Map(messages.filter((m) => joinedIds.has(m.id)).map((m) => [m.id, m])),
    [messages, joinedIds],
  );
  const liveSegments = useMemo(() => splitTrace(currentTrace, joinedMessages), [currentTrace, joinedMessages]);
  const forgetJoinedMessage = (messageId: string) => {
    if (joinedMessagesRef.current.some((j) => j.messageId === messageId)) {
      updateJoinedMessages(joinedMessagesRef.current.filter((j) => j.messageId !== messageId));
    }
  };
  /** Texto do rastro só se estende se a última entrada vier depois do último corte. */
  const canExtendLastTraceEntry = (trace: ThinkingTraceEntry[]) =>
    trace.length > lastBoundary(joinedMessagesRef.current);
  // text-delta accumulated since the last tool_call_start. When the next
  // tool_call_start arrives this buffer is moved into the trace as a text
  // entry (matches the API-side checkpoint logic).
  const pendingTextBufferRef = useRef('');
  // thinking-delta accumulated since the last tool_call_start (reasoning
  // tokens, fan-out narration). Always lives in the trace, never in the final
  // answer.
  const pendingReasoningRef = useRef('');
  const finalStreamingTextRef = useRef('');
  // SSE watchdog: detects when the backend silently stops responding (e.g. the
  // POST request never reached the server, agentic crashed without broadcast,
  // upstream provider hangs). Reset on every SSE event tied to the live
  // request; cleared on ai_response or onError. Generous default (3 min) so
  // legitimate slow-model executions never hit it — only true hangs do.
  // Must be greater than the agentic's STREAM_IDLE_MS so the backend gets
  // first crack at handling stalls with the wrap-up path.
  const watchdogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const SSE_WATCHDOG_MS = 180_000;
  // Botão Parar (AB#86888): enquanto o cancelamento não volta pelo SSE, o botão fica travado.
  // Se o servidor não fechar a resposta a tempo, ela é encerrada só na tela.
  const [isStopping, setIsStopping] = useState(false);
  const stopFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const STOP_FALLBACK_MS = 10_000;
  const [activeUsers, setActiveUsers] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showEmbedChatFlyout, setShowEmbedChatFlyout] = useState(false);
  // Bulk items cached from bulk_items SSE event — shows quick reply buttons above input
  const [bulkItems, setBulkItems] = useState<{ items: any[]; query: string; itemCount: number } | null>(null);

  // toolName → integration logo URL. Resolved from the MCP integrations
  // installed in the workspace; rendered in the trace next to each tool call.
  // Tools without a known integration (core / platform tools) fall back to
  // the Prodgy logo inside ThinkingTrace.
  const toolLogoMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const integration of mcpIntegrations) {
      if (!integration.logo_url) continue;
      for (const tool of integration.tools) {
        map.set(tool.name, integration.logo_url);
      }
    }
    return map;
  }, [mcpIntegrations]);
  const getToolLogo = useMemo(
    () => (toolName: string): string | null => toolLogoMap.get(toolName) ?? null,
    [toolLogoMap],
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const newlyCreatedChatRef = useRef<string | null>(null);

  /** Clear the SSE watchdog timer (called on ai_response, onError, or when
   * the user navigates away). */
  const clearWatchdog = () => {
    if (watchdogTimerRef.current) {
      clearTimeout(watchdogTimerRef.current);
      watchdogTimerRef.current = null;
    }
  };

  /** Push a synthetic assistant message that represents an error/interruption
   * inline in the chat timeline. Anchored to the failing interaction so the
   * user keeps context. Marked via metadata.is_error so the renderer can
   * style it as destructive without breaking the flow. */
  const pushAssistantErrorMessage = (text: string) => {
    const now = new Date().toISOString();
    const errorMessage: ChatMessageType = {
      id: `error-${Date.now()}`,
      session_id: '',
      user_id: 'ai',
      product_id: currentWorkspace?.id || '',
      message_type: 'ai',
      content: text,
      metadata: { is_error: true },
      created_at: now,
      updated_at: now,
    };
    setMessages(prev => [...prev, errorMessage]);
  };

  /** Arm (or reset) the SSE watchdog. Every backend event that proves the
   * stream is alive should call this. If the timer fires it means the
   * backend went silent without finishing — push an inline error into the
   * timeline and stop the thinking indicator so the user can retry. */
  const armWatchdog = () => {
    clearWatchdog();
    watchdogTimerRef.current = setTimeout(() => {
      console.warn('[PlaygroundView] SSE watchdog fired — no events for', SSE_WATCHDOG_MS, 'ms');
      setIsAIThinking(false);
      setCurrentTrace([]);
      setFinalStreamingText('');
      currentTraceRef.current = [];
      updateJoinedMessages([]);
      pendingTextBufferRef.current = '';
      pendingReasoningRef.current = '';
      finalStreamingTextRef.current = '';
      pushAssistantErrorMessage(
        t('playground.errors.streamTimeout', 'A IA não respondeu a tempo. Tente novamente.'),
      );
    }, SSE_WATCHDOG_MS);
  };

  /** Encerra a resposta em andamento na tela: some o "pensando", o trace e o texto parcial. */
  const endStreamingOnScreen = () => {
    clearWatchdog();
    if (stopFallbackTimerRef.current) {
      clearTimeout(stopFallbackTimerRef.current);
      stopFallbackTimerRef.current = null;
    }
    setIsStopping(false);
    setIsAIThinking(false);
    setCurrentTrace([]);
    setFinalStreamingText('');
    currentTraceRef.current = [];
    // A resposta parou: as mensagens mandadas no meio voltam ao lugar em que foram enviadas.
    updateJoinedMessages([]);
    pendingTextBufferRef.current = '';
    pendingReasoningRef.current = '';
    finalStreamingTextRef.current = '';
  };

  /** Nota neutra na timeline quando a resposta foi parada antes de qualquer texto. */
  const pushStoppedNote = () => {
    const now = new Date().toISOString();
    setMessages(prev => [
      ...prev,
      {
        id: `stopped-${Date.now()}`,
        session_id: '',
        user_id: 'ai',
        product_id: currentWorkspace?.id || '',
        message_type: 'ai',
        content: t('playground.chat.stopped'),
        created_at: now,
        updated_at: now,
      },
    ]);
  };

  /** Parar: aborta o turno no servidor; a resposta parcial chega depois pelo SSE. */
  const handleStop = async () => {
    if (!selectedChatId || isStopping) return;
    setIsStopping(true);
    try {
      const cancelled = await PlaygroundMultiChatService.cancelTurn(selectedChatId);
      if (!cancelled) {
        // Nada rodando no servidor (já terminou): só falta encerrar na tela.
        endStreamingOnScreen();
        return;
      }
      // O servidor abortou; se a resposta não fechar pelo SSE a tempo, fecha aqui.
      stopFallbackTimerRef.current = setTimeout(() => {
        endStreamingOnScreen();
        pushStoppedNote();
      }, STOP_FALLBACK_MS);
    } catch (err) {
      // O cancelamento não chegou ao servidor: o agente continua, e a resposta segue na tela.
      // Dizer que parou seria mentir — o usuário é avisado e pode tentar de novo.
      logger.error('[PlaygroundView] Failed to cancel the turn', { chatId: selectedChatId, err });
      setIsStopping(false);
      toast({
        title: t('playground.chat.stopFailed'),
        variant: 'destructive',
      });
    }
  };

  // Função para rolar para a última mensagem
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });
    }
  };

  // Carregar integrações com MCP tools
  useEffect(() => {
    const loadMcpIntegrations = async () => {
      const orgId = currentWorkspace?.organization_id || userProfile?.organization_id;
      if (!orgId) return;
      try {
        const integrations = await IntegrationBaseService.getActive(currentWorkspace?.id);
        const withMcp: McpIntegrationInfo[] = [];
        for (const ib of integrations) {
          // mcp_config pode estar na instância ou no template
          const mcpConfig = (ib as any).mcp_config || (ib.template as any)?.mcp_config;
          if (mcpConfig?.enabled && Array.isArray(mcpConfig.tools) && mcpConfig.tools.length > 0) {
            withMcp.push({
              id: ib.id,
              name: ib.name,
              label: ib.label || ib.template?.label || ib.name,
              title: ib.title || null,
              // Logo comes from the integration template (the brand of the
              // integration itself — Azure DevOps, Jira, etc.). The instance
              // logo_url is only used as a last-resort fallback for unusual
              // setups; in normal workspaces only the template carries it.
              logo_url: ib.template?.logo_url || ib.logo_url || null,
              tools: mcpConfig.tools.map((t: any) => ({ name: t.name, description: t.description || '' })),
            });
          }
        }
        setMcpIntegrations(withMcp);
      } catch (error) {
        console.error('[PlaygroundView] Error loading MCP integrations:', error);
      }
    };
    loadMcpIntegrations();
  }, [currentWorkspace?.organization_id, currentWorkspace?.id, userProfile?.organization_id]);

  // Load default LLM model name from organization
  useEffect(() => {
    const loadDefaultModel = async () => {
      const orgId = currentWorkspace?.organization_id || userProfile?.organization_id;
      if (!orgId) return;
      try {
        // Escolha do propósito CONVERSACIONAL — a que o Playground usa (AB#85432). O
        // modelo vem da organização, não do cadastro da integração: `settings.completion`
        // era a segunda fonte que este PBI eliminou.
        const escolha = await OrganizationService.getAiModels(orgId);
        const conversational = escolha?.conversational;
        if (!conversational?.integration_base_id) {
          setHasLlmConfigured(false);
          setDefaultModelName(null);
          return;
        }
        const integration = await IntegrationBaseService.getById(
          conversational.integration_base_id,
        );
        const completionModel = conversational.model;
        const label = integration?.label || integration?.name || '';
        const isProdgyProvider = /prodgy(\.ai)?/i.test(label);
        if (completionModel) {
          setDefaultModelName(isProdgyProvider ? label : `${label} · ${completionModel}`);
          setHasLlmConfigured(true);
        } else {
          setDefaultModelName(label || null);
          setHasLlmConfigured(false);
        }
      } catch (error) {
        console.error('[PlaygroundView] Error loading default model:', error);
        setHasLlmConfigured(false);
      }
    };
    loadDefaultModel();
  }, [currentWorkspace?.organization_id, userProfile?.organization_id]);

  // Auto-scroll quando mensagens mudarem, IA estiver pensando ou chat for alternado
  useEffect(() => {
    scrollToBottom();
  }, [messages, isAIThinking, selectedChatId]);

  // Tarefas em segundo plano (AB#86889): o indicador da conversa, a marca na lista e o aviso de
  // resposta que chegou noutra conversa.
  const [backgroundTasksOpen, setBackgroundTasksOpen] = useState(false);
  const backgroundTasks = useBackgroundTasks(selectedChatId, (reply) => {
    clearHistoryCache(reply.chatId);
    if (reply.chatId === selectedChatId) return; // a conversa aberta recebe pelo próprio SSE
    const title = chats.find((c) => c.id === reply.chatId)?.title;
    toast({
      title: title
        ? t('playground.backgroundTasks.replyElsewhere', { title })
        : t('playground.backgroundTasks.replyElsewhereUntitled'),
      action: (
        <ToastAction altText={t('playground.backgroundTasks.open')} onClick={() => selectChat(reply.chatId)}>
          {t('playground.backgroundTasks.open')}
        </ToastAction>
      ),
    });
  });

  // Tarefas rodando em outras conversas: a bolinha no botão do histórico. As da conversa aberta já
  // aparecem abaixo da caixa de mensagem.
  const otherChatsRunning = chats
    .filter((chat) => chat.id !== selectedChatId)
    .reduce((total, chat) => total + backgroundTasks.activityFor(chat), 0);

  /** A resposta de uma tarefa que terminou entra como mensagem nova, sem tocar no turno em andamento. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const appendBackgroundReply = (message: any) => {
    const createdAt = message.created_at || new Date().toISOString();
    const aiMessage: ChatMessageType = {
      id: message.id || crypto.randomUUID(),
      session_id: message.session_id,
      user_id: 'ai',
      product_id: currentWorkspace?.id || '',
      message_type: 'ai',
      content: message.content,
      metadata: message.metadata ?? undefined,
      created_at: createdAt,
      updated_at: createdAt,
    };
    let messageCount = 0;
    setMessages((prev) => {
      if (prev.some((m) => m.id === aiMessage.id)) return prev;
      const updated = [...prev, aiMessage];
      messageCount = updated.length;
      return updated;
    });
    if (selectedChatId && messageCount > 0) {
      addMessageToCache(selectedChatId, message);
      updateChatMetadata(selectedChatId, messageCount, message);
    }
    setShowEmptyState(false);
  };

  // SSE Hook para comunicação em tempo real (adaptar para usar chatId)
  const { connect, disconnect } = usePlaygroundSSE(
    currentWorkspace?.id || '', 
    selectedChatId || '', 
    {
    onUserMessage: (_data: any) => {
   },
    onAIThinking: () => {
      setIsAIThinking(true);
      armWatchdog();
      currentTraceRef.current = [];
      setCurrentTrace([]);
      updateJoinedMessages([]);
      pendingTextBufferRef.current = '';
      pendingReasoningRef.current = '';
      finalStreamingTextRef.current = '';
      setFinalStreamingText('');
    },
    onAgentSelected: (_data: any) => {
      armWatchdog();
      // No-op for the new trace UX — the trace itself is the visual marker
      // that the assistant is working. We could surface the agent name in the
      // trace title later if we add multi-agent execution back.
    },
    onAIThinkingDelta: (data: any) => {
      armWatchdog();
      // Reasoning tokens / fan-out narration. Live-render into the trace as a
      // mutable last text entry; the API persists this exact content.
      const text = data.text || data.data?.text || '';
      if (!text) return;
      pendingReasoningRef.current += text;
      // Reflect live in the visible trace by mutating the last text entry if
      // it is the reasoning buffer; otherwise append a new one.
      currentTraceRef.current = [...currentTraceRef.current];
      const last = currentTraceRef.current[currentTraceRef.current.length - 1];
      if (last && last.kind === 'text' && canExtendLastTraceEntry(currentTraceRef.current)) {
        currentTraceRef.current[currentTraceRef.current.length - 1] = { kind: 'text', content: last.content + text };
      } else {
        currentTraceRef.current.push({ kind: 'text', content: text });
      }
      setCurrentTrace(currentTraceRef.current.slice());
    },
    onAITextDelta: (data: any) => {
      armWatchdog();
      // Model's response text. Streams live below the trace; will be moved
      // into the trace if a tool_call_start follows (it was narration before
      // the tool, not the final answer).
      const text = data.text || data.data?.text || '';
      if (!text) return;
      pendingTextBufferRef.current += text;
      finalStreamingTextRef.current += text;
      setFinalStreamingText(finalStreamingTextRef.current);
    },
    onAITextReset: () => {
      // Backend (bulk fan-out) is about to emit a consolidated response that
      // supersedes whatever streamed so far. Clear the local buffers so the
      // next text-delta events become the canonical final answer.
      pendingTextBufferRef.current = '';
      finalStreamingTextRef.current = '';
      setFinalStreamingText('');
    },
    onToolCallStart: (data: any) => {
      armWatchdog();
      const toolCallId = data.toolCallId || data.data?.toolCallId || '';
      const toolName = data.toolName || data.data?.toolName || '';
      const args = data.args || data.data?.args;
      const integration = data.integration || data.data?.integration;
      const integrationLogoUrl = data.integrationLogoUrl || data.data?.integrationLogoUrl;

      // Retroactively reclassify text streamed since the last checkpoint as
      // narration: remove it from the streaming final answer and push it into
      // the trace. Mirrors the API-side bookkeeping in executeViaAgentic.
      const textToCommit = pendingTextBufferRef.current;
      pendingTextBufferRef.current = '';
      if (textToCommit) {
        finalStreamingTextRef.current = finalStreamingTextRef.current.slice(
          0,
          finalStreamingTextRef.current.length - textToCommit.length,
        );
        setFinalStreamingText(finalStreamingTextRef.current);
      }
      // Flush any reasoning tokens accumulated alongside.
      const reasoningToCommit = pendingReasoningRef.current;
      pendingReasoningRef.current = '';

      const newTrace = currentTraceRef.current.slice();
      if (textToCommit) {
        // If the last entry was reasoning that already absorbed text, merge.
        const last = newTrace[newTrace.length - 1];
        if (last && last.kind === 'text' && reasoningToCommit && canExtendLastTraceEntry(newTrace)) {
          // Reasoning already captured live; append the now-classified text.
          newTrace[newTrace.length - 1] = { kind: 'text', content: last.content + (last.content ? '\n\n' : '') + textToCommit };
        } else {
          newTrace.push({ kind: 'text', content: textToCommit });
        }
      }
      newTrace.push({
        kind: 'tool',
        toolCallId,
        toolName,
        args,
        integration,
        integrationLogoUrl,
        status: 'running',
      });
      currentTraceRef.current = newTrace;
      setCurrentTrace(newTrace);
    },
    onToolCallEnd: (data: any) => {
      armWatchdog();
      const toolCallId = data.toolCallId || data.data?.toolCallId || '';
      const toolName = data.toolName || data.data?.toolName || '';
      const resultPayload = data.result ?? data.data?.result;
      const newTrace = currentTraceRef.current.slice();
      for (let i = newTrace.length - 1; i >= 0; i--) {
        const entry = newTrace[i];
        if (entry.kind !== 'tool') continue;
        const matches = toolCallId
          ? entry.toolCallId === toolCallId
          : entry.toolName === toolName && entry.status === 'running';
        if (matches) {
          newTrace[i] = {
            ...entry,
            status: 'done',
            ...(resultPayload !== undefined ? { result: resultPayload } : {}),
          };
          break;
        }
      }
      currentTraceRef.current = newTrace;
      setCurrentTrace(newTrace);
    },
    onToolProgress: (data: any) => {
      // Uma espera longa (await_task) está viva: rearma o watchdog e mostra o tempo.
      armWatchdog();
      const toolCallId = data.toolCallId || data.data?.toolCallId || '';
      const elapsedMs = data.elapsedMs ?? data.data?.elapsedMs;
      const message = data.message || data.data?.message || '';
      const progress = typeof elapsedMs === 'number'
        ? t('playground.chat.waitingRun', { seconds: Math.round(elapsedMs / 1000) })
        : message;
      if (!toolCallId || !progress) return;
      const newTrace = applyToolProgress(currentTraceRef.current, toolCallId, progress);
      currentTraceRef.current = newTrace;
      setCurrentTrace(newTrace);
    },
    onAICancelled: () => {
      // Parado antes de qualquer texto: não há resposta para guardar.
      endStreamingOnScreen();
      pushStoppedNote();
    },
    onAIStreamFinish: () => {
      // Stream done — the pending text buffer is the final response (already
      // in finalStreamingText). Reasoning still buffered belongs to the trace.
      pendingTextBufferRef.current = '';
      pendingReasoningRef.current = '';
    },
    onBulkItems: (data: any) => {
      // Cache bulk items for quick reply buttons above input
      setBulkItems(data.data || data);
      setPaginationPage(0); // Reset pagination from previous sessions
    },
    onAIResponse: (data: any) => {
      // Resposta de uma tarefa em segundo plano (AB#86889): entra na conversa como mensagem nova,
      // sem encerrar um turno do usuário que esteja em andamento.
      const origin = data.message?.metadata?.origin;
      if (origin === 'background_resume' || origin === 'background_expired') {
        appendBackgroundReply(data.message);
        return;
      }
      clearWatchdog();
      // Snapshot the trace BEFORE clearing streaming state. O andamento da espera era só ao vivo.
      const finalTrace = stripToolProgress(currentTraceRef.current);
      const joined = joinedMessagesRef.current;
      updateJoinedMessages([]);
      if (stopFallbackTimerRef.current) {
        clearTimeout(stopFallbackTimerRef.current);
        stopFallbackTimerRef.current = null;
      }
      setIsStopping(false);

      setIsAIThinking(false);
      setCurrentTrace([]);
      setFinalStreamingText('');
      currentTraceRef.current = [];
      pendingTextBufferRef.current = '';
      pendingReasoningRef.current = '';
      finalStreamingTextRef.current = '';

      // Limpar flag de chat recém-criado ao receber primeira resposta
      if (newlyCreatedChatRef.current === selectedChatId) {
        newlyCreatedChatRef.current = null;
      }

      if (data.message) {
        const assistantTimestamp = data.timestamp || new Date().toISOString();
        const thinkingId = data.message.id || crypto.randomUUID();
        const thinkingMessage = (trace: ThinkingTraceEntry[], index: number, at: string): ChatMessageType => ({
          id: `thinking-${thinkingId}${index > 0 ? `-${index}` : ''}`,
          session_id: data.session_id,
          user_id: 'ai',
          product_id: currentWorkspace?.id || '',
          message_type: 'thinking',
          content: '',
          trace,
          created_at: at,
          updated_at: at,
        });

        const aiMessage: ChatMessageType = {
          id: data.message.id || crypto.randomUUID(),
          session_id: data.session_id,
          user_id: 'ai',
          product_id: currentWorkspace?.id || '',
          message_type: 'ai',
          content: data.message.content,
          metadata: data.message.sources ? { sources: data.message.sources } : undefined,
          created_at: assistantTimestamp,
          updated_at: assistantTimestamp,
        };
        let messageCount = 0;
        setMessages(prevMessages => {
          // As mensagens mandadas no meio do turno saem de onde estavam e voltam entre os trechos do
          // rastro em que entraram; as que foram para a fila vêm depois da resposta (AB#86889, 87271).
          // A mesma montagem refaz o turno quando a conversa é reaberta (AB#87296).
          const updated = arrangeTurn(prevMessages, aiMessage, finalTrace, joined, thinkingMessage);
          messageCount = updated.length;
          return updated;
        });

        // Cache + metadata fora do updater: chamar setState (updateChatMetadata)
        // ou mutar ref (addMessageToCache) dentro de um state updater roda na
        // fase de render e dispara o warning "Cannot update a component
        // (ChatBaseProvider) while rendering a different component".
        if (selectedChatId) {
          addMessageToCache(selectedChatId, data.message);
          updateChatMetadata(selectedChatId, messageCount, data.message);
        }

        // Esconder empty state quando receber mensagem
        setShowEmptyState(false);
      }
    },
    onUserJoined: (data: any) => {
      if (data.data.user_id) {
        setActiveUsers(prev => [...new Set([...prev, data.data.user_id])]);
      }
    },
    onUserLeft: (data: any) => {
      if (data.data.user_id) {
        setActiveUsers(prev => prev.filter(id => id !== data.data.user_id));
      }
    },
    onError: (errorEvent: any) => {
      endStreamingOnScreen();
      console.error('Erro SSE:', errorEvent);
      pushAssistantErrorMessage(t('playground.errors.general'));
    }
  });

  // Carregar histórico quando um chat for selecionado
  useEffect(() => {
    if (currentWorkspace?.id) {
      connect();
    }
    return () => {
      disconnect();
      clearWatchdog();
    };
  }, [connect, disconnect, currentWorkspace?.id]);

  // Buscar histórico de mensagens
  useEffect(() => {
    const fetchMessages = async () => {
  
      if (!currentWorkspace?.id || !selectedChatId) {
       setMessages([]);
        setShowEmptyState(true);
        return;
      }

      // Chat recem-criado: manter mensagens existentes (optimistic user message)
      if (newlyCreatedChatRef.current === selectedChatId) {
        setIsLoadingHistory(false);
        return;
      }

      try {
       setIsLoadingHistory(true);
        setError(null);
        setShowEmptyState(false);
        
        const history = await getChatHistory(selectedChatId);
       
        if (!history || history.length === 0) {
        setMessages([]);
          setShowEmptyState(true);
        } else {
          // Converter dados do backend para estrutura PlaygroundChatMessage. A resposta do agente
          // traz o rastro do turno (AB#87296), remontado como o bloco "Pensamento" antes dela.
          const storedTraces = new Map<string, StoredTurnTrace<ThinkingTraceEntry>>();
          const plainMessages: ChatMessageType[] = history.map((msg: any) => {
            if (msg.trace && typeof msg.trace === 'object') storedTraces.set(msg.id, msg.trace);
            return {
              id: msg.id,
              session_id: msg.session_id || '',
              user_id: msg.user_id,
              product_id: currentWorkspace.id,
              message_type: msg.message_type === 'assistant' ? 'ai' : msg.message_type,
              content: msg.content,
              metadata: msg.metadata ? {
                sources: msg.sources,
                ...msg.metadata
              } : undefined,
              created_at: msg.created_at,
              updated_at: msg.created_at
            };
          });
          const thinkingFor = (answer: ChatMessageType, at: string, index = 0): ChatMessageType => ({
            id: `thinking-${answer.id}${index > 0 ? `-${index}` : ''}`,
            session_id: answer.session_id,
            user_id: 'ai',
            product_id: answer.product_id,
            message_type: 'thinking',
            content: '',
            created_at: at,
            updated_at: at,
          });
          const convertedMessages = arrangeHistory(
            plainMessages,
            (m) => (m.message_type === 'ai' ? storedTraces.get(m.id) : undefined),
            (answer, entries, index, at) => ({ ...thinkingFor(answer, at, index), trace: entries }),
            (answer) => ({ ...thinkingFor(answer, answer.created_at), traceUnavailable: true }),
          );

         setMessages(convertedMessages);
          setShowEmptyState(convertedMessages.length === 0);
        }
      } catch (error) {
        console.error('Erro ao buscar mensagens:', error);
        setError(t('playground.errors.loadHistory'));
      } finally {
        setIsLoadingHistory(false);
      }
    };

    fetchMessages();
  }, [currentWorkspace?.id, selectedChatId, getChatHistory, t]);

  // Resumo de anexo longo em preparo (AB#87121, AC23): consulta a conversa de tempos em
  // tempos e troca só os anexos, até o resumo ficar pronto ou falhar. Vai direto ao
  // serviço porque o histórico em cache vale 5 minutos e devolveria o estado antigo.
  const summaryPending = useMemo(() => hasPendingSummary(messages), [messages]);
  useEffect(() => {
    if (!summaryPending || !selectedChatId) return;
    const chatId = selectedChatId;
    const timer = window.setInterval(async () => {
      const { data, error } = await PlaygroundMultiChatService.getChatHistoryWithChatId({ chatId });
      // Falhou: a próxima volta tenta de novo.
      if (error || !data) {
        logger.warn('[Playground] attachment summary refresh failed', { chatId, error });
        return;
      }
      const fresh = new Map(
        data.messages.map((m) => [m.id, (m.metadata as { attachments?: ChatAttachment[] } | undefined)?.attachments]),
      );
      setMessages((prev) => mergeAttachmentUpdates(prev, fresh));
      clearHistoryCache(chatId);
    }, SUMMARY_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [summaryPending, selectedChatId, clearHistoryCache]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sendMessage = async (overrideMessage?: string, consolidationMode?: 'analytical' | 'paginated' | 'detailed', paginatedItems?: any[], originalQuery?: string) => {
    const messageContent = (overrideMessage || input).trim();
    if (!messageContent || !user || !currentWorkspace?.id) return;
    // Os anexos vão só com a mensagem que a pessoa digitou, nunca com as automáticas (paginação).
    const files = overrideMessage ? [] : pendingFiles;

    setInput("");
    if (files.length > 0) setPendingFiles([]);

    // Optimistic UI: render user message instantly with a temp ID
    const tempId = `temp-${Date.now()}`;
    const optimisticMessage: ChatMessageType = {
      id: tempId,
      content: messageContent,
      session_id: '',
      user_id: user.id || '',
      product_id: currentWorkspace.id,
      message_type: 'user',
      created_at: new Date().toISOString(),
      ...(files.length > 0
        ? {
            metadata: {
              attachments: files.map((f) => ({ name: f.name, format: null, size_bytes: f.size, upload_token: '' })),
            },
          }
        : {}),
    };
    setMessages(prev => [...prev, optimisticMessage]);
    setShowEmptyState(false);

    // Com o agente ainda respondendo, a mensagem entra no que ele está fazendo (AB#86889, task
    // 87271): o rastro e o texto em andamento ficam como estão — a resposta final cobre as duas.
    const joinsRunningTurn = isAIThinking && !!selectedChatId;
    if (joinsRunningTurn) {
      updateJoinedMessages([
        ...joinedMessagesRef.current,
        { messageId: tempId, traceIndex: currentTraceRef.current.length },
      ]);
    } else {
      // Show AI thinking placeholder immediately (don't wait for SSE event)
      setIsAIThinking(true);
      setCurrentTrace([]);
      setFinalStreamingText('');
      currentTraceRef.current = [];
      pendingTextBufferRef.current = '';
      pendingReasoningRef.current = '';
      finalStreamingTextRef.current = '';
      // Arm watchdog right away. If the POST below succeeds the backend will
      // start sending SSE events that keep resetting the timer; if it never
      // gets there (network drop, server hung, etc.) the watchdog fires and
      // restores the UI so the user can retry.
      armWatchdog();
    }

    try {
      let chatId = selectedChatId;

      // Se nao ha chat selecionado, criar um novo automaticamente
      if (!chatId) {
        const newChat = await createChat({
          title: messageContent.length > 50 ? messageContent.substring(0, 50) + '...' : messageContent
        });
        if (!newChat) {
          // Rollback optimistic message
          setMessages(prev => prev.filter(m => m.id !== tempId));
          return;
        }
        chatId = newChat.id;
        newlyCreatedChatRef.current = chatId;
        selectChat(chatId);
      }

      // Os anexos sobem antes da mensagem: ela carrega a referência de cada um. Se algum
      // for recusado (formato, tamanho), nada é enviado e o usuário vê o motivo.
      let attachments: ChatAttachment[] = [];
      if (files.length > 0) {
        try {
          attachments = await Promise.all(
            files.map((file) => PlaygroundMultiChatService.uploadAttachment(chatId!, file)),
          );
        } catch (uploadError) {
          setMessages(prev => prev.filter(m => m.id !== tempId));
          forgetJoinedMessage(tempId);
          if (!joinsRunningTurn) {
            clearWatchdog();
            setIsAIThinking(false);
          }
          setInput(messageContent);
          setPendingFiles(files);
          toast({
            title: t('playground.attachments.uploadFailed'),
            description: uploadError instanceof Error ? uploadError.message : undefined,
            variant: "destructive",
          });
          return;
        }
      }

      const response = await PlaygroundMultiChatService.sendMessageWithChatId({
        message: messageContent,
        chatId: chatId,
        ...(attachments.length > 0 ? { attachments } : {}),
        ...(consolidationMode ? { consolidationMode } : {}),
        ...(paginatedItems ? { paginatedItems } : {}),
        ...(originalQuery ? { originalQuery } : {})
      });

      // Replace optimistic message with real data from backend
      if (response.data && response.data.user_message) {
        const userMessage = response.data.user_message as any;

        // A mensagem do meio do turno segue no mesmo ponto com o id definitivo; se foi para a fila,
        // roda num turno próprio e vai para depois da resposta.
        if (joinsRunningTurn) {
          const queued = response.data.delivery === 'queued';
          updateJoinedMessages(
            joinedMessagesRef.current.map((j) =>
              j.messageId === tempId ? { ...j, messageId: userMessage.id, queued } : j,
            ),
          );
        }

        setMessages(prev => prev.map(m => m.id === tempId ? {
          id: userMessage.id,
          content: userMessage.content,
          session_id: userMessage.session_id,
          user_id: userMessage.user_id,
          product_id: userMessage.product_id,
          message_type: userMessage.message_type,
          metadata: userMessage.metadata,
          created_at: userMessage.created_at,
        } : m));

        if (chatId) {
          addMessageToCache(chatId, userMessage);
          updateChatMetadata(chatId, messages.length + 1, userMessage);
        }

        analyticsService.trackEvent('playground_interaction', {
          query_length: messageContent.length,
          has_context: messages.length > 0
        });
        // O turno acabou de fechar ou roda noutro lugar: a mensagem espera e roda em seguida.
        if (response.data.delivery === 'queued') {
          toast({ title: t('playground.chat.queued') });
        }
      }
    } catch (error) {
      logger.error('[PlaygroundView] Failed to send the message', { error });
      setMessages(prev => prev.filter(m => m.id !== tempId));
      forgetJoinedMessage(tempId);
      // A falha desta mensagem não encerra o turno que já estava rodando.
      if (!joinsRunningTurn) {
        clearWatchdog();
        setIsAIThinking(false);
      }

      const isSubscriptionError = error instanceof Error &&
        (error.message.includes('Subscription') || error.message.includes('subscription'));

      if (isSubscriptionError) {
        toast({
          title: t('knowledge.errors.subscriptionRequired'),
          description: t('knowledge.errors.subscriptionRequiredDescription'),
          variant: "destructive",
        });
      } else {
        pushAssistantErrorMessage(t('playground.errors.sendMessage'));
      }
    }
  };

  // Pagination state for bulk items list
  const [paginationPage, setPaginationPage] = useState(0);
  const BULK_PAGE_SIZE = 10;

  const handleBulkChoice = (mode: 'analytical' | 'paginated' | 'detailed') => {
    const cached = bulkItems;
    if (!cached) return;

    if (mode === 'paginated') {
      const pageItems = cached.items.slice(0, BULK_PAGE_SIZE);
      const totalPages = Math.ceil(cached.items.length / BULK_PAGE_SIZE);
      setPaginationPage(1);
      // Label for chat display, original query passed separately for LLM context
      sendMessage(`📋 Lista paginada (página 1 de ${totalPages})`, 'paginated', pageItems, cached.query);
      return;
    }

    setBulkItems(null);
    const choiceLabel = mode === 'analytical' ? '📊 Resumo' : '📄 Documento completo';
    sendMessage(choiceLabel, mode, undefined, cached.query);
  };

  const handleBulkNextPage = () => {
    if (!bulkItems) return;

    const start = paginationPage * BULK_PAGE_SIZE;
    const end = Math.min(start + BULK_PAGE_SIZE, bulkItems.items.length);
    const pageItems = bulkItems.items.slice(start, end);
    const hasMore = end < bulkItems.items.length;
    const totalPages = Math.ceil(bulkItems.items.length / BULK_PAGE_SIZE);
    const currentPage = paginationPage + 1;

    sendMessage(`📋 Continuação (página ${currentPage} de ${totalPages})`, 'paginated', pageItems, bulkItems.query);

    if (hasMore) {
      setPaginationPage(prev => prev + 1);
    } else {
      setBulkItems(null);
      setPaginationPage(0);
    }
  };

  const handleBulkRefine = () => {
    const cached = bulkItems;
    setBulkItems(null);
    setPaginationPage(0);
    if (!cached) return;

    // Display "🔍 Refinar pesquisa" in chat, send detailed prompt to backend via originalQuery
    const refinePrompt = `Encontrei ${cached.itemCount} itens para "${cached.query}". ` +
      'Quais filtros posso usar para refinar essa pesquisa? ' +
      'Sugira opções baseadas nos tipos, categorias, responsáveis ou outros campos disponíveis nos dados.';

    sendMessage('🔍 Refinar pesquisa', undefined, undefined, refinePrompt);
  };

  const addAttachments = (picked: File[]) => {
    const { accepted, tooLarge } = splitBySize(picked);
    if (tooLarge.length > 0) {
      toast({
        title: t('playground.attachments.tooLarge'),
        description: tooLarge.map((f) => f.name).join(', '),
        variant: "destructive",
      });
    }
    if (accepted.length > 0) setPendingFiles(prev => [...prev, ...accepted]);
  };

  const removeAttachment = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Abre o anexo de uma mensagem numa aba nova (AB#87121). A aba abre já no clique —
  // depois do await o navegador trataria como pop-up — e recebe o link quando ele chega.
  const openAttachment = async (attachment: ChatAttachment) => {
    if (!selectedChatId) return;
    const tab = window.open('', '_blank');
    try {
      const url = await PlaygroundMultiChatService.attachmentViewUrl(selectedChatId, attachment.upload_token);
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener');
      }
    } catch {
      tab?.close();
      toast({
        title: t('playground.attachments.openFailed'),
        description: t('playground.attachments.openFailedHint'),
        variant: "destructive",
      });
    }
  };


  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Função para compartilhar chat em PDF
  const handleSharePDF = async () => {
    try {
      if (!currentWorkspace || messages.length === 0) return;

      const { default: jsPDF } = await import('jspdf');
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });

      const layout: PdfLayout = {
        margin:     18,
        pageWidth:  doc.internal.pageSize.width,
        pageHeight: doc.internal.pageSize.height,
        footerH:    12,
        contentW:   doc.internal.pageSize.width - 18 * 2,
        bottomEdge: doc.internal.pageSize.height - 12 - 6,
      };
      const colors = PDF_COLORS;

      const userName = sanitizeForPdf(user?.user_metadata?.full_name || user?.email || 'Você');
      const wsName   = sanitizeForPdf(currentWorkspace.name);
      const dateStr  = new Date().toLocaleString(getLocale());

      let y = renderHeader(doc, layout, colors, wsName, dateStr);

      const visibleMessages = messages.filter(
        (m) => m.message_type !== 'thinking' && m.content?.trim()
      );

      for (let idx = 0; idx < visibleMessages.length; idx++) {
        const message  = visibleMessages[idx];
        const isUser   = message.message_type === 'user' || message.role === 'user';
        const label    = isUser ? userName : wsName;
        const segments = parseContentSegments(message.content);
        y = renderMessageBlock(doc, layout, colors, segments, label, isUser, y);

        if (idx < visibleMessages.length - 1) {
          doc.setDrawColor(...colors.light);
          doc.setLineWidth(0.2);
          doc.line(layout.margin + 10, y - 1, layout.pageWidth - layout.margin - 10, y - 1);
          y += 2;
        }
      }

      drawFooters(doc, layout, colors);

      const safeWs  = currentWorkspace.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const dateTag = new Date().toISOString().split('T')[0];
      doc.save(`chat-${safeWs}-${dateTag}.pdf`);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
    }
  };

  // Chat dropdown & inline rename hooks (must be before early returns)
  const [isChatDropdownOpen, setIsChatDropdownOpen] = useState(false);
  const chatDropdownRef = useRef<HTMLDivElement>(null);
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null);

  const cancelEditingChat = () => {
    setEditingChatId(null);
    setEditingTitle("");
  };

  const handleDeleteChat = async () => {
    if (!deletingChatId) return;
    // If the user deletes the chat they're currently viewing, send them back to
    // the empty playground state — otherwise the UI keeps showing messages for
    // a chat that no longer exists.
    const wasCurrent = deletingChatId === selectedChatId;
    const success = await deleteChat(deletingChatId);
    if (success) {
      setDeletingChatId(null);
      if (wasCurrent) {
        setMessages([]);
        setShowEmptyState(true);
        selectChat(null);
      }
    }
  };

  // Close dropdown on outside click — but ignore clicks inside Radix portals
  // (kebab DropdownMenu, AlertDialog) which render outside chatDropdownRef.
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target) return;
      if (
        target.closest('[role="menu"]') ||
        target.closest('[role="dialog"]') ||
        target.closest('[role="alertdialog"]')
      ) {
        return;
      }
      if (chatDropdownRef.current && !chatDropdownRef.current.contains(target)) {
        setIsChatDropdownOpen(false);
        cancelEditingChat();
      }
    };
    if (isChatDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isChatDropdownOpen]);

  if (!currentWorkspace) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-65px)]">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">{t('playground.no_workspace.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('playground.no_workspace.description')}</p>
        </div>
      </div>
    );
  }

  const streamingProseClasses = cn(
    "prose prose-sm max-w-none break-words text-foreground/70",
    "prose-neutral dark:prose-invert",
    "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
    "prose-headings:text-foreground/80 prose-headings:font-semibold",
    "prose-strong:text-foreground/80 prose-strong:font-semibold",
    "prose-p:my-2 prose-li:my-1",
    "prose-pre:bg-muted prose-pre:rounded-lg",
    "prose-code:before:content-none prose-code:after:content-none",
    "prose-a:text-primary/80 prose-a:no-underline hover:prose-a:underline",
    "prose-thead:bg-muted/50",
    "prose-th:border prose-th:border-border prose-th:px-3 prose-th:py-2 prose-th:text-foreground/70",
    "prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2",
  );

  const startEditingChat = (chat: { id: string; title: string }) => {
    setEditingChatId(chat.id);
    setEditingTitle(chat.title);
    setTimeout(() => editInputRef.current?.select(), 0);
  };

  const saveEditingChat = async () => {
    if (!editingChatId || !editingTitle.trim()) {
      cancelEditingChat();
      return;
    }
    const success = await renameChat(editingChatId, editingTitle.trim());
    if (success) cancelEditingChat();
  };

  /** Uma mensagem da conversa — da lista e das que entraram no meio do turno (AB#86889, 87271). */
  const renderChatMessage = (message: ChatMessageType) => {
    if (message.message_type === 'thinking') {
      return (
        <ThinkingTrace
          key={message.id}
          entries={message.trace || []}
          getToolLogo={getToolLogo}
          unavailableNote={message.traceUnavailable ? t('playground.chat.traceUnavailable') : undefined}
        />
      );
    }

    const isAgent = message.message_type === "ai";

    const author = isAgent
      ? {
          id: 'playground-ai',
          name: currentWorkspace?.name || 'Chat',
          logo_url: currentWorkspace?.logo_url || undefined,
          avatar_url: currentWorkspace?.logo_url || undefined
        }
      : messageAuthors[message.user_id] || {
          id: message.user_id,
          name: user?.email || 'Usuário',
          avatar_url: user?.user_metadata?.avatar_url || undefined,
          email: user?.email || undefined,
          user_metadata: {
            avatar_url: user?.user_metadata?.avatar_url || undefined,
            full_name: user?.user_metadata?.full_name || user?.email || 'Usuário'
          }
        };

    const adaptedMessage = {
      id: message.id,
      message: {
        content: message.content,
        type: isAgent ? "ai" as const : "human" as const
      },
      type: isAgent ? "ai" as const : "human" as const,
      created_at: message.created_at,
      user_id: message.user_id,
      agent_id: isAgent ? 'ai' : undefined
    };

    return (
      <ChatMessage
        key={message.id}
        message={adaptedMessage}
        author={author}
        isAgent={isAgent}
        isError={!!message.metadata?.is_error}
        attachments={isAgent ? undefined : message.metadata?.attachments}
        onOpenAttachment={openAttachment}
      />
    );
  };

  return (
    <div className="flex flex-col h-[calc(100vh-105px)] -ml-[2rem] -mr-[calc(2rem+1rem)] lg:-mr-[calc(2rem+2rem)] -mt-6">
        {/* Top bar — only visible when there are messages */}
        <div className={cn(
          "flex items-center justify-between px-2 py-1.5 border-b border-border/40",
          messages.length === 0 && !isAIThinking && !selectedChatId && "hidden"
        )}>
          {/* Chat selector dropdown */}
          <div className="relative flex items-center gap-2" ref={chatDropdownRef}>
            <button
              onClick={() => setIsChatDropdownOpen(prev => !prev)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-muted/50 transition-colors text-sm max-w-[300px]"
            >
              <MessageSquare className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
              <span className="truncate font-medium text-foreground/80">
                {selectedChat?.title || t('playground.view.newChat')}
              </span>
              <BackgroundTasksMarker count={otherChatsRunning} />
              <ChevronDown className={cn(
                "h-3 w-3 text-muted-foreground/50 shrink-0 transition-transform duration-200",
                isChatDropdownOpen && "rotate-180"
              )} />
            </button>

            {/* Dropdown panel */}
            {isChatDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-72 bg-background border border-border/60 rounded-lg shadow-lg z-50 overflow-hidden">
                {/* Chat list */}
                <div className="max-h-[280px] overflow-y-auto">
                  {chats.length === 0 ? (
                    <div className="px-3 py-4 text-xs text-muted-foreground/50 text-center">
                      {t('playground.chatList.noChats')}
                    </div>
                  ) : (
                    chats.slice(0, 15).map((chat) => (
                      <div
                        key={chat.id}
                        className={cn(
                          "group w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted/50 transition-colors text-left cursor-pointer",
                          selectedChatId === chat.id && "bg-muted/60 font-medium"
                        )}
                        onClick={() => {
                          if (editingChatId !== chat.id) {
                            selectChat(chat.id);
                            setIsChatDropdownOpen(false);
                          }
                        }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          startEditingChat(chat);
                        }}
                      >
                        <MessageSquare className="h-3 w-3 text-muted-foreground/40 shrink-0" />
                        {editingChatId === chat.id ? (
                          <input
                            ref={editInputRef}
                            type="text"
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveEditingChat();
                              if (e.key === 'Escape') cancelEditingChat();
                            }}
                            onBlur={saveEditingChat}
                            className="flex-1 min-w-0 bg-transparent border-b border-primary/50 outline-none text-foreground/80 text-sm py-0"
                            autoFocus
                          />
                        ) : (
                          <>
                            <span className="flex-1 min-w-0 truncate text-foreground/80">{chat.title}</span>
                            <BackgroundTasksMarker count={chat.id === selectedChatId ? 0 : backgroundTasks.activityFor(chat)} />
                          </>
                        )}
                        {editingChatId !== chat.id && (
                          // Selection dot (when not hovered) + action buttons (on hover):
                          // edit (rename) on the left, delete on the right. Single-click on
                          // the row navigates to the chat, so the rename affordance must be
                          // an explicit button — onDoubleClick is never reachable.
                          <div className="ml-auto shrink-0 flex items-center gap-0.5">
                            {selectedChatId === chat.id && (
                              <span className="w-1.5 h-1.5 rounded-full bg-primary group-hover:hidden" />
                            )}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); startEditingChat(chat); }}
                              aria-label={t('playground.chatList.renameChat')}
                              title={t('playground.chatList.renameChat')}
                              className="w-5 h-5 flex items-center justify-center rounded text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:bg-muted/80 hover:text-foreground"
                            >
                              <Edit2 className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setDeletingChatId(chat.id); }}
                              aria-label={t('playground.chatList.deleteChat')}
                              title={t('playground.chatList.deleteChat')}
                              className="w-5 h-5 flex items-center justify-center rounded text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:bg-muted/80 hover:text-red-600"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
                {/* View all link — sempre visível quando houver pelo menos 1 chat */}
                {chats.length > 0 && (
                  <button
                    onClick={() => {
                      toggleSidebar();
                      setIsChatDropdownOpen(false);
                    }}
                    className="w-full px-3 py-2 text-xs text-muted-foreground/60 hover:text-foreground hover:bg-muted/30 transition-colors border-t border-border/30 text-center"
                  >
                    {t('playground.chatList.showFullHistory')}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Right action buttons */}
          <div className="flex items-center gap-0.5 ml-auto">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setMessages([]);
                setShowEmptyState(true);
                selectChat(null);
              }}
              className="h-7 w-7 text-foreground/70 hover:text-foreground hover:bg-muted/60 border border-border rounded-lg"
              title={t('playground.view.newChat')}
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
            {/* Delete current chat — only visible when a saved chat is open.
                Routes through the existing confirmation dialog + handleDeleteChat,
                which already handles "if this is the current chat, go home". */}
            {selectedChatId && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDeletingChatId(selectedChatId)}
                className="h-7 w-7 text-foreground/70 hover:text-red-600 hover:bg-muted/60"
                title={t('playground.chatList.deleteChat')}
                aria-label={t('playground.chatList.deleteChat')}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
            {activeUsers.length > 0 && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mr-2">
                <Users className="h-3 w-3" />
                <span>{activeUsers.length}</span>
              </div>
            )}
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSharePDF}
                className="h-7 w-7 text-foreground/70 hover:text-foreground hover:bg-muted/60"
                title={t('playground.view.shareConversation')}
              >
                <Share className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowEmbedChatFlyout(true)}
              className="h-7 w-7 text-foreground/70 hover:text-foreground hover:bg-muted/60"
              title={t('playground.embedWidget.embedButton')}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Mensagem de erro — apenas falhas de carregamento de histórico aparecem
            como banner no topo. Erros de fluxo de chat (timeout, SSE error,
            POST falho) viram mensagens inline na timeline via
            pushAssistantErrorMessage. */}
        {error && (
          <div className="px-4 pt-2">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>
        )}

        {/* Aviso: nenhum LLM configurado — chat bloqueado */}
        {!hasLlmConfigured && (
          <div className="px-4 pt-2">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <span className="font-medium">{t('playground.view.noLlmTitle')}</span>
                <span className="block text-xs mt-0.5 text-muted-foreground">
                  {t('playground.view.noLlmDescription')}
                </span>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Content area */}
        <div className="flex-1 overflow-hidden">
          {isLoadingHistory ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-muted-foreground/30 rounded-full animate-typing" />
                <span className="w-1.5 h-1.5 bg-muted-foreground/30 rounded-full animate-typing [animation-delay:0.2s]" />
                <span className="w-1.5 h-1.5 bg-muted-foreground/30 rounded-full animate-typing [animation-delay:0.4s]" />
              </div>
            </div>
          ) : messages.length > 0 || isAIThinking ? (
            <div className="h-full flex flex-col">
              <ScrollArea className="flex-1">
                <div className="flex flex-col justify-end min-h-full space-y-1 py-4 max-w-3xl mx-auto">
                  {messages
                    .filter(message => {
                      if (!message) return false;
                      // Com o turno rodando, a mensagem mandada no meio é desenhada junto do rastro.
                      if (isAIThinking && joinedIds.has(message.id)) return false;
                      // Keep thinking rows even when their content string is empty —
                      // the trace lives on `message.trace`.
                      if (message.message_type === 'thinking') {
                        return message.traceUnavailable || (Array.isArray(message.trace) && message.trace.length > 0);
                      }
                      return !!message.content && message.content.trim() !== "";
                    })
                    .map(renderChatMessage)}
                  {/* AI streaming: live thinking trace + streaming final answer */}
                  {isAIThinking && (
                    <>
                      {liveSegments.map((segment, index) => {
                        const after = segment.messageAfter ? messageById.get(segment.messageAfter) : undefined;
                        return (
                          <Fragment key={segment.messageAfter ?? 'live-tail'}>
                            {segment.entries.length > 0 && (
                              <ThinkingTrace
                                entries={segment.entries}
                                isStreaming={index === liveSegments.length - 1}
                                getToolLogo={getToolLogo}
                              />
                            )}
                            {after && renderChatMessage(after)}
                          </Fragment>
                        );
                      })}
                      {liveSegments[liveSegments.length - 1].entries.length === 0 && !finalStreamingText && (
                        <div className="w-full py-1.5">
                          <div className="flex gap-3 px-4 max-w-none">
                            <Avatar className="h-9 w-9 border border-border/20 shadow-sm shrink-0 mt-0.5">
                              {currentWorkspace?.logo_url ? (
                                <AvatarImage src={currentWorkspace.logo_url} alt={currentWorkspace.name} className="object-cover" />
                              ) : (
                                <AvatarFallback className="bg-primary/10 text-primary border-primary/20 text-sm font-medium">
                                  {currentWorkspace?.name?.[0]?.toUpperCase() || 'P'}
                                </AvatarFallback>
                              )}
                            </Avatar>
                            <div className="flex items-center gap-1 py-2">
                              <span className="w-1.5 h-1.5 bg-muted-foreground/40 rounded-full animate-[typing_1.4s_ease-in-out_infinite]" />
                              <span className="w-1.5 h-1.5 bg-muted-foreground/40 rounded-full animate-[typing_1.4s_ease-in-out_0.2s_infinite]" />
                              <span className="w-1.5 h-1.5 bg-muted-foreground/40 rounded-full animate-[typing_1.4s_ease-in-out_0.4s_infinite]" />
                            </div>
                          </div>
                        </div>
                      )}
                      {finalStreamingText && (
                        <div className="w-full py-1 group">
                          <div className="flex gap-3 px-4 max-w-none">
                            <Avatar className="h-9 w-9 border border-border/20 shadow-sm shrink-0 mt-0.5">
                              {currentWorkspace?.logo_url ? (
                                <AvatarImage src={currentWorkspace.logo_url} alt={currentWorkspace.name} className="object-cover" />
                              ) : (
                                <AvatarFallback className="bg-primary/10 text-primary border-primary/20 text-sm font-medium">
                                  {currentWorkspace?.name?.[0]?.toUpperCase() || 'P'}
                                </AvatarFallback>
                              )}
                            </Avatar>
                            <div className="flex-1 min-w-0 space-y-0.5">
                              <div className="flex items-center gap-2">
                                <span className="text-[13px] font-semibold text-foreground/80">{currentWorkspace?.name || 'IA'}</span>
                              </div>
                              <StreamingMarkdown content={finalStreamingText} className={streamingProseClasses} />
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Foi para a fila: roda num turno próprio, depois desta resposta. */}
                      {joinedMessages
                        .filter((j) => j.queued)
                        .map((j) => messageById.get(j.messageId))
                        .map((message) => message && renderChatMessage(message))}
                    </>
                  )}
                  <div ref={messagesEndRef} className="h-4" />
                </div>
              </ScrollArea>

              {/* Input area — bottom anchored when there are messages */}
              <div className="shrink-0 px-4 pt-3 pb-3 border-t border-border/40">
                <div className="max-w-3xl mx-auto">
                  {/* Quick reply buttons for bulk processing choice */}
                  {bulkItems && !isAIThinking && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {paginationPage > 0 ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs rounded-full"
                            onClick={handleBulkNextPage}
                          >
                            ➡️ {t('playground.bulk.next', 'Próximo')}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs rounded-full text-muted-foreground"
                            onClick={() => { setBulkItems(null); setPaginationPage(0); }}
                          >
                            ✅ {t('playground.bulk.done', 'Finalizar')}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs rounded-full"
                            onClick={() => handleBulkChoice('analytical')}
                          >
                            📊 {t('playground.bulk.analytical', 'Resumo')}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs rounded-full"
                            onClick={() => handleBulkChoice('paginated')}
                          >
                            📋 {t('playground.bulk.paginated', 'Lista paginada')}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs rounded-full"
                            onClick={() => handleBulkChoice('detailed')}
                          >
                            📄 {t('playground.bulk.detailed', 'Documento completo')}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs rounded-full text-muted-foreground"
                            onClick={() => handleBulkRefine()}
                          >
                            🔍 {t('playground.bulk.refine', 'Refinar pesquisa')}
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                  <ChatComposer
                    value={input}
                    onChange={setInput}
                    onSubmit={() => sendMessage()}
                    onKeyDown={handleKeyDown}
                    disabled={!currentWorkspace?.id || !hasLlmConfigured}
                    isResponding={isAIThinking}
                    onStop={handleStop}
                    stopDisabled={isStopping || !selectedChatId}
                    hasBackgroundTasks={backgroundTasks.runningCount > 0}
                    onStopBackground={() => setBackgroundTasksOpen(true)}
                    canSend={!!currentWorkspace?.id && !!input.trim() && hasLlmConfigured}
                    files={pendingFiles}
                    onPickFiles={addAttachments}
                    onRemoveFile={removeAttachment}
                  />
                  <div className="relative mt-1.5 min-h-[18px] flex items-center justify-center">
                    {/* Tarefas em segundo plano da conversa aberta, logo abaixo da caixa (AB#86889, task 87270) */}
                    <div className="absolute left-0 top-0">
                      <BackgroundTasksIndicator
                        tasks={backgroundTasks.running}
                        onCancel={backgroundTasks.cancel}
                        open={backgroundTasksOpen}
                        onOpenChange={setBackgroundTasksOpen}
                      />
                    </div>
                    {defaultModelName && (
                      <span className="text-[11px] text-muted-foreground/50">{defaultModelName}</span>
                    )}
                    <TooltipProvider delayDuration={200}>
                      <div className="absolute right-0 top-0 flex items-center gap-1">
                        {mcpIntegrations.slice(0, 4).map((integration) => (
                          <Tooltip key={integration.id}>
                            <TooltipTrigger asChild>
                              <div className="h-5 w-5 rounded border border-border/30 bg-muted/20 flex items-center justify-center">
                                {integration.logo_url ? (
                                  <img
                                    src={integration.logo_url}
                                    alt={integration.label}
                                    className="h-3 w-3 object-contain"
                                    referrerPolicy="no-referrer"
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                  />
                                ) : (
                                  <span className="text-[9px] font-medium text-muted-foreground">
                                    {integration.label?.[0]?.toUpperCase()}
                                  </span>
                                )}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs">{integration.label}</TooltipContent>
                          </Tooltip>
                        ))}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={() => setMcpToolsOpen(true)}
                              aria-label={t('playground.mcpTools.openDialog')}
                              className="h-5 w-5 rounded border border-border/30 bg-muted/20 flex items-center justify-center hover:bg-muted/40 transition-colors"
                            >
                              <MoreHorizontal className="h-3 w-3 text-muted-foreground" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">{t('playground.mcpTools.openDialog')}</TooltipContent>
                        </Tooltip>
                      </div>
                    </TooltipProvider>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Empty state — centered input, clean like Claude */
            <div className="h-full flex flex-col items-center justify-center px-4 -mt-20">
              <div className="w-full max-w-3xl space-y-4">
                {/* Logo + Greeting */}
                <div className="text-center">
                  <h2 className="text-2xl font-medium text-foreground/80">
                    {t('playground.view.emptyStateTitle')}
                  </h2>
                </div>

                {/* Large input — Claude style */}
                <div className="w-full">
                  <ChatComposer
                    variant="hero"
                    value={input}
                    onChange={setInput}
                    onSubmit={() => sendMessage()}
                    onKeyDown={handleKeyDown}
                    disabled={!currentWorkspace?.id || !hasLlmConfigured}
                    isResponding={isAIThinking}
                    onStop={handleStop}
                    stopDisabled={isStopping || !selectedChatId}
                    hasBackgroundTasks={backgroundTasks.runningCount > 0}
                    onStopBackground={() => setBackgroundTasksOpen(true)}
                    canSend={!!currentWorkspace?.id && !!input.trim() && hasLlmConfigured}
                    files={pendingFiles}
                    onPickFiles={addAttachments}
                    onRemoveFile={removeAttachment}
                  />
                </div>

                {/* Below input: history + actions (left) | integration logos (right) */}
                <div className="flex items-center justify-between">
                  {/* Left: chat history dropdown + action buttons */}
                  <div className="flex items-center gap-1">
                    <div className="relative" ref={chatDropdownRef}>
                      <button
                        onClick={() => setIsChatDropdownOpen(prev => !prev)}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border/50 hover:bg-muted/50 transition-colors text-sm"
                      >
                        <MessageSquare className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="text-muted-foreground text-sm">
                          {selectedChat?.title || t('playground.chatList.title')}
                        </span>
                        <BackgroundTasksMarker count={otherChatsRunning} />
                        <ChevronDown className={cn(
                          "h-3 w-3 text-muted-foreground shrink-0 transition-transform duration-200",
                          isChatDropdownOpen && "rotate-180"
                        )} />
                      </button>

                      {isChatDropdownOpen && (
                        <div className="absolute top-full left-0 mt-1 w-72 bg-background border border-border/60 rounded-lg shadow-lg z-50 overflow-hidden">
                          <div className="max-h-[280px] overflow-y-auto">
                            {chats.length === 0 ? (
                              <div className="px-3 py-4 text-xs text-muted-foreground/50 text-center">
                                {t('playground.chatList.noChats')}
                              </div>
                            ) : (
                              chats.slice(0, 15).map((chat) => (
                                <div
                                  key={chat.id}
                                  className={cn(
                                    "group w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted/50 transition-colors text-left cursor-pointer",
                                    selectedChatId === chat.id && "bg-muted/60 font-medium"
                                  )}
                                  onClick={() => {
                                    if (editingChatId !== chat.id) {
                                      selectChat(chat.id);
                                      setIsChatDropdownOpen(false);
                                    }
                                  }}
                                  onDoubleClick={(e) => {
                                    e.stopPropagation();
                                    startEditingChat(chat);
                                  }}
                                >
                                  <MessageSquare className="h-3 w-3 text-muted-foreground/40 shrink-0" />
                                  {editingChatId === chat.id ? (
                                    <input
                                      ref={editInputRef}
                                      type="text"
                                      value={editingTitle}
                                      onChange={(e) => setEditingTitle(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') saveEditingChat();
                                        if (e.key === 'Escape') cancelEditingChat();
                                      }}
                                      onBlur={saveEditingChat}
                                      className="flex-1 min-w-0 bg-transparent border-b border-primary/50 outline-none text-foreground/80 text-sm py-0"
                                      autoFocus
                                    />
                                  ) : (
                                    <>
                                      <span className="flex-1 min-w-0 truncate text-foreground/80">{chat.title}</span>
                                      <BackgroundTasksMarker count={chat.id === selectedChatId ? 0 : backgroundTasks.activityFor(chat)} />
                                    </>
                                  )}
                                  {editingChatId !== chat.id && (
                                    // Edit (rename) + delete inline buttons — single-click on
                                    // the row navigates, so rename needs an explicit button.
                                    <div className="ml-auto shrink-0 flex items-center gap-0.5">
                                      {selectedChatId === chat.id && (
                                        <span className="w-1.5 h-1.5 rounded-full bg-primary group-hover:hidden" />
                                      )}
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); startEditingChat(chat); }}
                                        aria-label={t('playground.chatList.renameChat')}
                                        title={t('playground.chatList.renameChat')}
                                        className="w-5 h-5 flex items-center justify-center rounded text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:bg-muted/80 hover:text-foreground"
                                      >
                                        <Edit2 className="h-3 w-3" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setDeletingChatId(chat.id); }}
                                        aria-label={t('playground.chatList.deleteChat')}
                                        title={t('playground.chatList.deleteChat')}
                                        className="w-5 h-5 flex items-center justify-center rounded text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:bg-muted/80 hover:text-red-600"
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                          {chats.length > 0 && (
                            <button
                              onClick={() => {
                                toggleSidebar();
                                setIsChatDropdownOpen(false);
                              }}
                              className="w-full px-3 py-2 text-xs text-muted-foreground/60 hover:text-foreground hover:bg-muted/30 transition-colors border-t border-border/30 text-center"
                            >
                              {t('playground.chatList.showFullHistory')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowEmbedChatFlyout(true)}
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      title={t('playground.embedWidget.embedButton')}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  {/* Right: MCP tools — integration logos + details button */}
                  <TooltipProvider delayDuration={200}>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-muted-foreground">{t('playground.mcpTools.title')}</span>
                      {mcpIntegrations.map((integration) => (
                        <Tooltip key={integration.id}>
                          <TooltipTrigger asChild>
                            <div className="h-7 w-7 rounded-md border border-border/40 bg-muted/20 flex items-center justify-center cursor-default hover:bg-muted/40 transition-colors">
                              {integration.logo_url ? (
                                <img
                                  src={integration.logo_url}
                                  alt={integration.label}
                                  className="h-4 w-4 object-contain"
                                  referrerPolicy="no-referrer"
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                              ) : (
                                <span className="text-[10px] font-medium text-muted-foreground">
                                  {integration.label?.[0]?.toUpperCase()}
                                </span>
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[280px]">
                            <div className="space-y-1.5">
                              <p className="font-medium text-xs">{integration.label}</p>
                              <div className="flex flex-wrap gap-1">
                                {integration.tools.map((tool) => (
                                  <span
                                    key={tool.name}
                                    className="inline-block px-1.5 py-0.5 text-[10px] bg-muted rounded text-muted-foreground"
                                  >
                                    {tool.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => setMcpToolsOpen(true)}
                            aria-label={t('playground.mcpTools.openDialog')}
                            className="h-7 w-7 rounded-md border border-border/40 bg-muted/20 flex items-center justify-center hover:bg-muted/40 transition-colors"
                          >
                            <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <p className="text-xs">{t('playground.mcpTools.openDialog')}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TooltipProvider>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer: provider · model — fixado no bottom da página */}
        {defaultModelName && messages.length === 0 && !isAIThinking && (
          <div className="shrink-0 flex justify-center pb-3 pointer-events-none">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-black">
              <span className="text-[11px] text-white">{defaultModelName}</span>
            </div>
          </div>
        )}

      {/* ChatList Flyout */}
      <ChatList isOpen={isSidebarOpen} onClose={() => toggleSidebar()} backgroundCountFor={(chat) => (chat.id === selectedChatId ? 0 : backgroundTasks.activityFor(chat))} />
      
      {/* Flyout Embedar Chat */}
      <EmbedChatFlyout
        isOpen={showEmbedChatFlyout}
        onClose={() => setShowEmbedChatFlyout(false)}
        workspaceId={currentWorkspace?.id || ''}
        workspaceName={currentWorkspace?.name || ''}
      />

      {/* MCP Tools dialog — integrations, core tools, config */}
      <McpToolsDialog
        open={mcpToolsOpen}
        onOpenChange={setMcpToolsOpen}
        mcpIntegrations={mcpIntegrations}
      />

      {/* Confirmação de exclusão de chat (acionada pelo kebab no dropdown inline) */}
      <AlertDialog open={!!deletingChatId} onOpenChange={(open) => { if (!open) setDeletingChatId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('playground.chatList.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('playground.chatList.deleteConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteChat}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('playground.chatList.deleteChat')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}

// Componente principal que envolve com o Provider
export default function PlaygroundView() {
  return (
    <ChatBaseProvider>
      <PlaygroundViewInternal />
    </ChatBaseProvider>
  );
}
